<script setup lang="ts">
  // Ported from cartadigital-pos app/components/pos/order/PosOrderWorkspace.vue.
  // Catalog is fully in-memory (menuStore) — no async item fetching needed.
  import { computed, getCurrentInstance, ref, watch } from 'vue';
  import { useDebounceFn } from '@vueuse/core';
  import { OrderType } from '@/enums/OrderEnums';
  import { IItem } from '@/interfaces/IItem';
  import { AlertType } from '@/interfaces/IAlert';
  import { PosV2Error } from '@/domain/pos/pos-error';
  import type { IPosCartLine } from '@/domain/pos/pos-types';
  import { menuItemMatchesSearch } from '@/domain/pos/menu-item-search';
  import { useMenuStore } from '@/composition/use-menu';
  import { useModal } from '@/composition/use-modal';
  import { usePosMenuModel } from '@/composition/use-pos-menu-model';
  import { useUi } from '@/composition/use-ui';
  import { usePosDraftsStore } from '@/stores/pos-drafts.store';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosV2ItemModal, {
    IPosV2ItemModalResult,
  } from '@/components/pos-v2/modals/PosV2ItemModal.vue';
  import PosV2CategoryBar from './PosV2CategoryBar.vue';
  import PosV2Cart from './PosV2Cart.vue';
  import PosV2ItemsGrid from './PosV2ItemsGrid.vue';
  import PosV2TopBar from './PosV2TopBar.vue';

  const posDrafts = usePosDraftsStore();
  const menuStore = useMenuStore();
  const ui = useUi();
  const modals = useModal();

  // vue-i18n in script (Vue 2.7): translate through the component proxy.
  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const draft = computed(() => posDrafts.activeDraft);

  /** PosV2Error carries an i18n key + params; anything else falls back. */
  function toastPosError(error: unknown, fallback: string) {
    if (error instanceof PosV2Error) {
      ui.showToast(AlertType.Error, i18nT(error.i18nKey, error.params));
      return;
    }
    ui.serverError(error, fallback);
  }

  const menuModel = usePosMenuModel(
    computed(() => draft.value?.type ?? OrderType.Seat)
  );

  // Mobile view state (menu or cart)
  const mobileView = ref<'menu' | 'cart'>('menu');
  const cartCount = computed(() => draft.value?.cartLines.length ?? 0);

  //#region Search

  const menuSearch = ref('');
  const debouncedMenuSearch = ref('');
  const updateDebouncedMenuSearch = useDebounceFn((value: string) => {
    debouncedMenuSearch.value = value;
  }, 200);

  watch(menuSearch, (value) => {
    updateDebouncedMenuSearch(value);
  });

  const isSearching = computed(
    () => debouncedMenuSearch.value.trim().length > 0
  );

  function clearMenuSearch() {
    menuSearch.value = '';
    debouncedMenuSearch.value = '';
  }

  const searchResults = computed<IItem[]>(() => {
    if (!isSearching.value) return [];
    const q = debouncedMenuSearch.value;
    return menuModel.allItems.value.filter((it) =>
      menuItemMatchesSearch(it, q)
    );
  });

  const displayedItems = computed<IItem[]>(() =>
    isSearching.value ? searchResults.value : menuModel.items.value
  );

  const itemsEmptyText = computed(() =>
    isSearching.value ? i18nT('posV2.noResults') : i18nT('posV2.emptyCategory')
  );

  function handleCategorySelect(id: number | null) {
    // Clear on the raw input (not debounced): a quick tap on a category chip
    // must not leave a search that "activates" a moment later.
    if (menuSearch.value.trim().length > 0) {
      clearMenuSearch();
    }
    menuModel.selectedCategoryId.value = id;
  }

  //#endregion

  //#region Item add/edit flows

  // Re-entry guard: a rapid double-tap on an item/cart line must not stack a
  // second options dialog on top of the open one.
  let itemModalOpen = false;

  async function openItemModal(
    item: IItem,
    editingLine?: IPosCartLine
  ): Promise<IPosV2ItemModalResult | null> {
    if (itemModalOpen) return null;
    itemModalOpen = true;
    try {
      return await modals.showDynamicDialog<IPosV2ItemModalResult | null>(
        PosV2ItemModal,
        { item, editingLine },
        'pos-item-options',
        { width: 900 }
      );
    } finally {
      itemModalOpen = false;
    }
  }

  async function handleItemClick(item: IItem) {
    // Disabled entries render greyed but stay clickable; block adds here.
    if (menuModel.disabledItemIds.value.has(item.id)) {
      ui.showToast(AlertType.Error, i18nT('posV2.errItemDisabled'));
      return;
    }

    const currentDraft = draft.value;
    if (!currentDraft) return;
    const draftId = currentDraft.id;

    try {
      if (item.hasOptions) {
        const res = await openItemModal(item);
        if (!res) return;
        if (!posDrafts.drafts.find((d) => d.id === draftId)) return;

        posDrafts.addCartLine(
          draftId,
          item,
          res.optionValues,
          res.quantity,
          res.userComment
        );
      } else {
        posDrafts.addCartLine(draftId, item, {}, 1);
      }
    } catch (error) {
      toastPosError(error, i18nT('posV2.itemAddError'));
    }
  }

  async function handleEditLine(line: IPosCartLine) {
    const currentDraft = draft.value;
    if (!currentDraft) return;
    const draftId = currentDraft.id;

    const item = menuStore.itemMap?.[line.itemId];
    if (!item) {
      ui.showToast(AlertType.Error, i18nT('posV2.errItemUnavailable'));
      return;
    }

    try {
      const res = await openItemModal(item, line);
      if (!res) return;
      if (!posDrafts.drafts.find((d) => d.id === draftId)) return;

      posDrafts.editCartLine(
        draftId,
        line.index,
        res.optionValues,
        res.quantity,
        res.userComment
      );
    } catch (error) {
      toastPosError(error, i18nT('posV2.itemSaveError'));
    }
  }

  //#endregion
</script>

<template>
  <div class="pos-v2-order-workspace">
    <!-- Mobile bottom navigation -->
    <div class="mobile-nav">
      <button
        type="button"
        class="mobile-nav-button"
        :class="{ active: mobileView === 'menu' }"
        @click="mobileView = 'menu'"
      >
        <PosUiIcon name="silverware-fork-knife" />
        <span>{{ $t('posV2.menu') }}</span>
      </button>
      <button
        type="button"
        class="mobile-nav-button"
        :class="{ active: mobileView === 'cart' }"
        @click="mobileView = 'cart'"
      >
        <PosUiIcon name="cart-outline" />
        <span>{{ $t('posV2.cart') }}</span>
        <PosUiBadge
          v-if="cartCount > 0"
          :label="cartCount"
          color="primary"
          variant="solid"
          size="xs"
          class="mobile-nav-badge"
        />
      </button>
    </div>

    <!-- Main workspace -->
    <div class="workspace-main">
      <!-- Left: menu section -->
      <div
        class="workspace-left"
        :class="{ 'mobile-hidden': mobileView === 'cart' }"
      >
        <PosV2TopBar />

        <div class="menu-section">
          <div class="menu-search">
            <PosUiInput
              :value="menuSearch"
              icon="magnify"
              :placeholder="$t('posV2.searchProductPlaceholder')"
              size="lg"
              :clearable="true"
              class="menu-search-input"
              @input="(v) => (menuSearch = v)"
            />
            <PosUiBadge
              v-if="isSearching"
              color="neutral"
              variant="soft"
              size="sm"
              class="menu-search-count"
              :label="`${searchResults.length}`"
            />
          </div>

          <PosV2CategoryBar
            :categories="menuModel.categories.value"
            :selected-category-id="menuModel.selectedCategoryId.value"
            :loading="!menuModel.isReady.value"
            :is-searching="isSearching"
            :search-query="menuSearch"
            @search-pill-click="clearMenuSearch"
            @select="handleCategorySelect"
          />

          <PosV2ItemsGrid
            :items="displayedItems"
            :loading="!menuModel.isReady.value"
            :empty-text="itemsEmptyText"
            :disabled-item-ids="menuModel.disabledItemIds.value"
            @item-click="handleItemClick"
          />
        </div>
      </div>

      <!-- Right: cart -->
      <div
        class="workspace-right"
        :class="{ 'mobile-hidden': mobileView === 'menu' }"
      >
        <PosV2Cart @edit-line="handleEditLine" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .pos-v2-order-workspace {
    @apply tw-relative tw-flex tw-h-full tw-flex-col tw-overflow-hidden tw-bg-canvas;

    .mobile-nav {
      @apply tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-z-30 tw-flex tw-border-t tw-border-neutral-200 tw-bg-white lg:tw-hidden;

      .mobile-nav-button {
        @apply tw-relative tw-flex tw-min-h-[56px] tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-gap-1 tw-py-2 tw-text-neutral-500 tw-transition-colors;

        &.active {
          @apply tw-bg-orange-50 tw-text-orange-600;
        }

        span {
          @apply tw-text-xs tw-font-medium;
        }
      }

      .mobile-nav-badge {
        @apply tw-absolute tw-right-1/4 tw-top-1;
      }
    }

    .workspace-main {
      /* Flush below lg to match the view workspace (no neutral gap under the tab strip) */
      @apply tw-flex tw-flex-1 tw-gap-0 tw-overflow-hidden tw-p-0 tw-pb-[72px] lg:tw-gap-2 lg:tw-p-2 lg:tw-pb-2;
    }

    .workspace-left {
      /* overflow-clip (not hidden): hidden boxes can still be scrolled
         programmatically (focus/scrollIntoView), which used to shift the
         whole pane left permanently when top-bar content overflowed. */
      @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-overflow-clip;

      &.mobile-hidden {
        @apply tw-hidden lg:tw-flex;
      }
    }

    .menu-section {
      /* min-h-0 is load-bearing: overflow-clip (unlike hidden) is not a
         scroll container, so the flexbox auto-minimum would keep this pane
         at content height, pushing the items grid past the viewport and
         killing its internal scrolling (items became unreachable). */
      @apply tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-clip;
    }

    .menu-search {
      @apply tw-flex tw-items-center tw-gap-2 tw-border-b tw-border-neutral-200 tw-bg-white tw-px-2 tw-py-2;

      .menu-search-input {
        @apply tw-flex-1;
      }

      .menu-search-count {
        @apply tw-flex-shrink-0;
      }
    }

    .workspace-right {
      @apply tw-flex tw-w-full tw-flex-shrink-0 tw-flex-col tw-overflow-hidden lg:tw-w-80 xl:tw-w-96;

      &.mobile-hidden {
        @apply tw-hidden lg:tw-flex;
      }
    }
  }
</style>
